import React from "react";
import {
  Flag,
  Calendar,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
} from "lucide-react";

export default function MilestoneSummaryCard({
  milestones,
}) {
  if (!milestones) return null;

  const {
    total = 0,
    planned = 0,
    completed = 0,
    cancelled = 0,
    latest = [],
  } = milestones;

  const completionRate =
    total === 0
      ? 0
      : Math.round(
          (completed / total) * 100
        );

  const stat = (
    Icon,
    label,
    value,
    color
  ) => (
    <div className="flex items-center gap-3">
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}
      >
        <Icon className="w-5 h-5" />
      </div>

      <div>
        <div className="text-sm text-gray-500">
          {label}
        </div>

        <div className="text-xl font-semibold">
          {value}
        </div>
      </div>
    </div>
  );

  return (
    <div className="rounded-2xl border bg-white p-6">

      {/* Header */}

      <div className="flex items-center justify-between">

        <div>

          <h2 className="text-lg font-semibold">
            Milestone Summary
          </h2>

          <p className="text-sm text-gray-500">
            Major project checkpoints
          </p>

        </div>

        <Flag className="w-6 h-6 text-indigo-600" />

      </div>

      {/* Progress */}

      <div className="mt-6">

        <div className="flex justify-between text-sm mb-2">

          <span className="text-gray-500">
            Completion
          </span>

          <span className="font-medium">
            {completionRate}%
          </span>

        </div>

        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">

          <div
            className="h-full rounded-full bg-indigo-600 transition-all"
            style={{
              width: `${completionRate}%`,
            }}
          />

        </div>

      </div>

      {/* Stats */}

      <div className="grid grid-cols-2 gap-5 mt-6">

        {stat(
          Flag,
          "Total",
          total,
          "bg-indigo-50 text-indigo-600"
        )}

        {stat(
          Clock,
          "Planned",
          planned,
          "bg-amber-50 text-amber-600"
        )}

        {stat(
          CheckCircle2,
          "Completed",
          completed,
          "bg-emerald-50 text-emerald-600"
        )}

        {stat(
          XCircle,
          "Cancelled",
          cancelled,
          "bg-rose-50 text-rose-600"
        )}

      </div>

      {/* Recent */}

      <div className="mt-6 border-t pt-5">

        <div className="font-medium text-gray-800 mb-3">
          Recent Milestones
        </div>

        {latest.length === 0 ? (

          <div className="text-sm text-gray-500">
            No milestones have been created yet.
          </div>

        ) : (

          <div className="space-y-3">

            {latest.slice(0, 3).map((m) => (

              <div
                key={m._id}
                className="rounded-xl border p-3"
              >

                <div className="flex items-start justify-between">

                  <div>

                    <div className="font-medium">
                      {m.title}
                    </div>

                    {m.summary && (
                      <div className="text-sm text-gray-500 mt-1">
                        {m.summary}
                      </div>
                    )}

                  </div>

                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      m.status === "completed"
                        ? "bg-emerald-100 text-emerald-700"
                        : m.status === "cancelled"
                        ? "bg-rose-100 text-rose-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {m.status}
                  </span>

                </div>

                <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">

                  {m.targetDate && (

                    <div className="flex items-center gap-1">

                      <Calendar className="w-3.5 h-3.5" />

                      {new Date(
                        m.targetDate
                      ).toLocaleDateString()}

                    </div>

                  )}

                  <div className="flex items-center gap-1">

                    <Eye className="w-3.5 h-3.5" />

                    {m.visibility === "client"
                      ? "Client"
                      : "Internal"}

                  </div>

                </div>

              </div>

            ))}

          </div>

        )}

      </div>

    </div>
  );
}